import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './App.css';
import { useAuth0 } from "@auth0/auth0-react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import config from './config';
import FAQ from './faq';
import Blogs from './Blogs';
import BlogArticle from './BlogArticle';
import Settings from './Settings';
import MyProfile from './MyProfile';
import MyEnrollments from './MyEnrollments';
import MySessions from './MySessions';
import MySessionPage from './MySessionPage';
import PracticeTasks from './PracticeTasks';
import Courses from './Courses';
import CourseDetails from './CourseDetails';
import Home from './Home';
import PracticeTests from './PracticeTestsPage';
import { ExamCenter } from './ExamCenter';
import { ExamQuiz } from './ExamQuiz';
import { ExamAttemptReview } from './ExamAttemptReview';
import { PracticeTestQuiz } from './PracticeTestQuiz';
import { PracticeTestResults } from './PracticeTestResults';
import { HomeProvider } from './HomeContext';
import ProductDetails from './ProductDetails';
import Compare from './Compare';
import Dashboard from './components/Dashboard';
import ManageCategories from './admin/ManageCategories';
import ManageCategoriesV2 from './admin/ManageCategoriesV2';
import ManageClassrooms from './admin/ManageClassrooms';
import ManageCourses from './admin/ManageCourses';
import ManageCoursePricing from './admin/ManageCoursePricing';
import ManageExams from './admin/ManageExams';
import ManageAssignments from './admin/ManageAssignments';
import ManageSubmissions from './admin/ManageSubmissions';
import ManageTeachingAssignments from './admin/ManageTeachingAssignments';
import ManageTeacherCourseAssignments from './admin/ManageTeacherCourseAssignments';
import ManageSchedule from './admin/ManageSchedule';
import ManageScheduleDetail from './admin/ManageScheduleDetail';
import ManageSessions from './admin/ManageSessions';
import ManageSessionDetail from './admin/ManageSessionDetail';
import AssignmentsHub from './AssignmentsHub';
import AssignmentHomework from './AssignmentHomework';
import ManageQuestions from './admin/ManageQuestions';
import ManageDiagrams from './admin/ManageDiagrams';
import ManageCategoryDiagrams from './admin/ManageCategoryDiagrams';
import ManageDynamicComponents from './admin/ManageDynamicComponents';
import ManageSiteContent from './admin/ManageSiteContent';
import AdminLayout from './admin/AdminLayout';
import Billing from './Billing';
import SubscriptionSuccess from './SubscriptionSuccess';
import BookManagement from './admin/BookManagement';
import ManageFreeResources from './admin/ManageFreeResources';
import ManageDocuments from './admin/ManageDocuments';
import ManageEmailTemplates from './admin/ManageEmailTemplates';
import ManageOrganizations from './admin/ManageOrganizations';
import ManageOrganizationMemberships from './admin/ManageOrganizationMemberships';
import ManageOrganizationMembershipRoles from './admin/ManageOrganizationMembershipRoles';
import ManageOrganizationMembersAndRoles from './admin/ManageOrganizationMembersAndRoles';
import ManageOrganizationRequests from './admin/ManageOrganizationRequests';
import ManagePlatformUserRoles from './admin/ManagePlatformUserRoles';
import SuperAdminPlatformUsers from './admin/SuperAdminPlatformUsers';
import ReportTeacherHours from './admin/ReportTeacherHours';
import ReportStudentSessions from './admin/ReportStudentSessions';
import SubmissionReview from './admin/SubmissionReview';
import ContactUs from './ContactUs';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import DataDeletion from './DataDeletion';
import { initializeAnalytics, trackPageView } from './analytics';
import OrganizationPreDashboard from './OrganizationPreDashboard';
import CustomerLayout from './CustomerLayout';
import OrganizationOnboarding from './OrganizationOnboarding';
import OrganizationInviteAccept from './OrganizationInviteAccept';
import AdminDashboard from './admin/AdminDashboard';
import SuperAdminDashboard from './admin/SuperAdminDashboard';
import TeacherDashboard from './admin/TeacherDashboard';
import { clearOrganizationContext, getOrganizationContext, setOrganizationContext } from './orgContext';
import { filterNavItems, getMembershipRoles, getUserRoles, mergeRoles, settingsDropdownLinks } from './navigationConfig';
import { shouldRedirectAdminAccess } from './adminAccess';


const TRACKED_EXACT_PATHS = new Set([
  '/',
  '/courses',
  '/practice',
  '/blogs',
  '/compare',
  '/faq',
  '/contact',
]);

const TRACKED_PREFIX_PATHS = [
  '/course-details/',
  '/practice-tests/',
  '/exam-center/',
  '/exam/',
  '/exam-attempt/',
  '/practice-test/',
  '/practice-result/',
  '/product/',
  '/blogs/',
];

const shouldTrackRoute = (pathname: string): boolean => {
  if (TRACKED_EXACT_PATHS.has(pathname)) {
    return true;
  }

  return TRACKED_PREFIX_PATHS.some(prefix => pathname.startsWith(prefix));
};


function RouteAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!shouldTrackRoute(location.pathname)) {
      return;
    }

    initializeAnalytics();

    const path = `${location.pathname}${location.search}`;
    trackPageView(path);
  }, [location.pathname, location.search]);

  return null;
}

function RouterLocationSync({ onPathChange }: { onPathChange: (pathname: string, search: string) => void }) {
  const location = useLocation();

  useEffect(() => {
    onPathChange(location.pathname, location.search);
  }, [location.pathname, location.search, onPathChange]);

  return null;
}


const isAdminRoute = (pathname: string) =>
  pathname === '/admin' ||
  pathname === '/teacher-dashboard' ||
  pathname === '/super-admin' ||
  pathname.startsWith('/super-admin/') ||
  pathname.startsWith('/manage-') ||
  pathname.startsWith('/admin/') ||
  pathname === '/books' ||
  pathname === '/reports' ||
  pathname.startsWith('/reports/');
const isAdminTopBarHiddenRoute = (pathname: string) => isAdminRoute(pathname);
const ADMIN_NAV_ROLES = ['superadmin', 'owner', 'principal', 'teacher', 'staff', 'admin'];
const STUDENT_NAV_ROLES = ['student'];

const hasAnyRoleMatch = (roles: string[], requiredRoles: string[]): boolean => {
  const normalized = new Set(roles.map((role) => role.trim().toLowerCase()));

  return requiredRoles.some((requiredRole) => normalized.has(requiredRole.trim().toLowerCase()));
};

const redirectAdminAccess = () => {
  const currentPath = window.location.pathname;
  const fallbackPath = '/analytics';

  if (currentPath === fallbackPath || currentPath === '/analytics?pre=1') {
    return;
  }

  if (!shouldRedirectAdminAccess(currentPath)) {
    return;
  }

  window.location.assign(fallbackPath);
};

function App() {
  const { loginWithRedirect, isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);
  const [currentSearch, setCurrentSearch] = useState(window.location.search);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [membershipRoles, setMembershipRoles] = useState<string[]>([]);
  const [platformRoles, setPlatformRoles] = useState<string[]>([]);

  const userRoles = useMemo(() => mergeRoles(getUserRoles(user), membershipRoles, platformRoles), [membershipRoles, platformRoles, user]);
  const isStudentOnlyAccount = isAuthenticated && hasAnyRoleMatch(userRoles, STUDENT_NAV_ROLES) && !hasAnyRoleMatch(userRoles, ADMIN_NAV_ROLES);
  const visibleSettingsLinks = useMemo(() => filterNavItems(settingsDropdownLinks, isAuthenticated, userRoles), [isAuthenticated, userRoles]);

  const reservedTopLevelPaths = new Set([
    'courses', 'course-details', 'practice', 'practice-tests', 'exam-center', 'exam', 'exam-attempt',
    'practice-test', 'practice-result', 'analytics', 'my-enrollments', 'faq', 'contact', 'privacy',
    'terms', 'data-deletion', 'blogs', 'product', 'compare', 'profile', 'settings', 'manage-categories',
    'manage-classrooms', 'manage-assignments', 'manage-courses', 'manage-course-pricing', 'manage-exams', 'manage-questions',
    'manage-diagrams', 'manage-category-diagrams', 'manage-dynamic-components', 'manage-free-resources',
    'manage-documents', 'manage-email-templates', 'manage-organizations', 'manage-organization-memberships',
    'manage-organization-members-and-roles', 'manage-organization-membership-roles', 'manage-organization-requests', 'manage-platform-user-roles',
    'super-admin', 'reports', 'admin', 'billing', 'books', 'subscription-success', 'organization-setup', 'organization-invite'
  ]);
  const pathSegments = currentPathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0] ?? '';
  const adminRootPaths = new Set([
    'manage-organizations',
    'manage-organization-memberships',
    'manage-organization-members-and-roles',
    'manage-organization-membership-roles',
    'manage-organization-requests',
    'manage-platform-user-roles',
    'super-admin',
    'reports',
    'manage-categories',
    'manage-courses',
    'manage-course-pricing',
    'manage-classrooms',
    'manage-assignments',
    'manage-exams',
    'manage-questions',
    'manage-diagrams',
    'manage-category-diagrams',
    'manage-dynamic-components',
    'manage-free-resources',
    'manage-documents',
    'manage-email-templates',
    'books'
  ]);
  const isAdminPage =
    adminRootPaths.has(firstSegment) ||
    currentPathname.startsWith('/admin/');
  const routeOrganizationSlug =
    pathSegments.length >= 2 && !reservedTopLevelPaths.has(pathSegments[0])
      ? pathSegments[0]
      : '';
  const contextOrganizationSlug = getOrganizationContext()?.organizationSlug ?? '';
  const activeOrganizationSlug = routeOrganizationSlug || contextOrganizationSlug;
  const orgAwarePath = (path: string) =>
    isAuthenticated && activeOrganizationSlug ? `/${activeOrganizationSlug}${path}` : path;
  const studentPortalRootPaths = new Set([
    'analytics', 'courses', 'course-details', 'practice', 'assignments', 'my-sessions', 'my-session',
    'practice-tests', 'exam-center', 'exam', 'exam-attempt', 'practice-test', 'practice-result',
    'faq', 'contact', 'blogs', 'my-enrollments', 'profile', 'settings', 'billing'
  ]);
  const isStudentPortalRoute = (pathname: string) => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return false;
    }

    const [firstSegment, secondSegment] = segments;
    if (studentPortalRootPaths.has(firstSegment)) {
      return true;
    }

    if (segments.length < 2) {
      return false;
    }

    return !reservedTopLevelPaths.has(firstSegment) && studentPortalRootPaths.has(secondSegment);
  };
  const isPrePortalDashboardRoute = currentPathname === '/analytics' && (currentSearch === '?pre=1' || currentSearch === '?pre=true');
  const shouldRenderFullTopBar = !isAdminRoute(currentPathname) && !isStudentPortalRoute(currentPathname) && !isPrePortalDashboardRoute;
  const shouldRenderMinimalTopBar = isPrePortalDashboardRoute;

  const withAdminLayout = (element: React.ReactElement) => (
    <AdminLayout>{element}</AdminLayout>
  );

  const withStudentLayout = (element: React.ReactElement) => (
    <CustomerLayout>{element}</CustomerLayout>
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMenuOpen(false);
        setSettingsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isStudentOnlyAccount && isAdminRoute(currentPathname)) {
      window.location.assign('/courses');
      return;
    }
  }, [currentPathname, isStudentOnlyAccount]);

  useEffect(() => {
    const axiosInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const pathname = window.location.pathname;

        if (status === 403 && isAdminRoute(pathname)) {
          redirectAdminAccess();
        }

        return Promise.reject(error);
      }
    );

    const originalFetch = window.fetch.bind(window);
    const fetchWrapper = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await originalFetch(input, init);
        if (response.status === 403 && isAdminRoute(window.location.pathname)) {
          redirectAdminAccess();
        }
        return response;
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 403 && isAdminRoute(window.location.pathname)) {
          redirectAdminAccess();
        }
        throw error;
      }
    };

    window.fetch = fetchWrapper as typeof window.fetch;

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const menu = document.getElementById('mobile-menu');
      const hamburger = document.getElementById('hamburger-button');
      
      if (menuOpen && menu && hamburger && !menu.contains(target) && !hamburger.contains(target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMembershipRoles([]);
      setPlatformRoles([]);
      clearOrganizationContext();
      return;
    }

    let cancelled = false;

    const loadMembershipRoles = async () => {
      try {
        const token = await getAccessTokenSilently();

        const membershipsResponse = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (membershipsResponse.ok) {
          const organizations = await membershipsResponse.json();
          if (!cancelled) {
            setMembershipRoles(getMembershipRoles(organizations));
          }
        }

        const platformRolesResponse = await fetch(`${config.apiBaseUrl}/api/organizations/platform-user-roles`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!platformRolesResponse.ok) {
          if (!cancelled) {
            setPlatformRoles([]);
          }
          return;
        }

        const platformRolesData = await platformRolesResponse.json();
        if (!cancelled && Array.isArray(platformRolesData)) {
          const resolvedPlatformRoles = platformRolesData
            .filter((role: any) => role && typeof role === 'object')
            .map((role: any) => {
              const rawRoleKey = role.roleKey || role.RoleKey || role.role || role.name;
              return typeof rawRoleKey === 'string' && rawRoleKey.trim().length > 0 ? rawRoleKey.trim() : '';
            })
            .filter((role: string) => role.length > 0)
            .map((role: string) => role.toLowerCase());

          setPlatformRoles(resolvedPlatformRoles);
        }
      } catch (error) {
        console.warn('Unable to load organization membership roles', error);
      }
    };

    void loadMembershipRoles();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!activeOrganizationSlug || !isAuthenticated) {
      return;
    }

    const existingContext = getOrganizationContext();
    if (existingContext?.organizationSlug?.toLowerCase() === activeOrganizationSlug.toLowerCase()) {
      return;
    }

    let cancelled = false;

    const syncOrganizationContextFromSlug = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const organizations = await response.json();
        if (!Array.isArray(organizations)) {
          return;
        }

        const matchedOrganization = organizations.find(
          (item: any) => String(item.organizationSlug || '').toLowerCase() === activeOrganizationSlug.toLowerCase()
        );

        if (!cancelled && matchedOrganization?.organizationId && matchedOrganization?.organizationSlug) {
          setOrganizationContext({
            organizationId: Number(matchedOrganization.organizationId),
            organizationSlug: String(matchedOrganization.organizationSlug),
          });
        }
      } catch (error) {
        console.warn('Unable to sync organization context from URL slug', error);
      }
    };

    syncOrganizationContextFromSlug();

    return () => {
      cancelled = true;
    };
  }, [activeOrganizationSlug, isAuthenticated, getAccessTokenSilently]);

  return (
    <HomeProvider>
      <Router>
        <RouterLocationSync onPathChange={(pathname, search) => {
          setCurrentPathname(pathname);
          setCurrentSearch(search);
        }} />
        <RouteAnalyticsTracker />
        <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {shouldRenderFullTopBar && (
            <header style={{
              background: '#222',
              color: '#fff',
              padding: '0 0 0 0',
              marginBottom: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                maxWidth: 1200,
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                height: 64
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                  <img
                    src="/Icon-ExamMasters2.png"
                    alt="Exam Masters"
                    style={{
                      width: isMobile ? 24 : 30,
                      height: isMobile ? 24 : 30,
                      borderRadius: 6,
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    fontWeight: 700,
                    fontSize: isMobile ? 18 : 24,
                    letterSpacing: 1,
                    whiteSpace: 'nowrap'
                  }}>Exam Masters</div>
                </div>

                {/* Mobile Hamburger Menu */}
                {isMobile ? (
                  <>
                    <button
                      id="hamburger-button"
                      onClick={() => setMenuOpen(!menuOpen)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        fontSize: 28,
                        cursor: 'pointer',
                        padding: 8
                      }}
                    >
                      ☰
                    </button>

                    {/* Mobile Menu Dropdown */}
                    {menuOpen && (
                      <div
                        id="mobile-menu"
                        style={{
                        position: 'absolute',
                        top: 64,
                        left: 0,
                        background: '#222',
                        width: '100%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                        padding: '12px 0'
                      }}>
                        {isStudentOnlyAccount ? (
                          <>
                            <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Home</Link>
                            <Link to={orgAwarePath('/analytics')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Dashboard</Link>
                            <Link to={orgAwarePath('/courses')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Courses</Link>
                            <Link to={orgAwarePath('/practice')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Questions</Link>
                            <Link to={orgAwarePath('/assignments')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Assignments</Link>
                            <Link to={orgAwarePath('/practice-tests')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Practice Tests</Link>
                            <Link to={orgAwarePath('/my-enrollments')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>My Enrollments</Link>
                            <Link to={orgAwarePath('/blogs')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Blogs</Link>
                            <Link to={orgAwarePath('/faq')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Faq</Link>
                            <Link to={orgAwarePath('/contact')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Contact Us</Link>
                          </>
                        ) : (
                          <>
                            <Link to="/" onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Home</Link>
                            <Link to={orgAwarePath('/courses')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Courses</Link>
                            <Link to={orgAwarePath('/practice')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Questions</Link>
                            <Link to={orgAwarePath('/assignments')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Assignments</Link>
                            <Link to={orgAwarePath('/practice-tests')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Practice Tests</Link>
                            <Link to={orgAwarePath('/blogs')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Blogs</Link>
                            <Link to={orgAwarePath('/faq')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Faq</Link>
                            <Link to={orgAwarePath('/contact')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Contact Us</Link>
                          </>
                        )}

                        {isAuthenticated && (
                          <>
                            {!isStudentOnlyAccount && (
                              <Link to={orgAwarePath('/analytics')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>Analytics</Link>
                            )}

                            <div>
                              <div
                                onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
                                style={{
                                  display: 'block',
                                  color: '#fff',
                                  padding: '12px 24px',
                                  fontSize: 16,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  backgroundColor: settingsMenuOpen ? '#333' : 'transparent'
                                }}
                              >
                                Settings {settingsMenuOpen ? '▲' : '▼'}
                              </div>
                              {settingsMenuOpen && (
                                <div style={{ backgroundColor: '#333', paddingLeft: '12px' }}>
                                  {visibleSettingsLinks.map((link) => (
                                    <Link
                                      key={link.to}
                                      to={link.to}
                                      onClick={() => { setMenuOpen(false); setSettingsMenuOpen(false); }}
                                      style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 14, textAlign: 'left' }}
                                    >
                                      {link.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div style={{ padding: '12px 24px' }}>
                              <button
                                onClick={() => {
                                  setMenuOpen(false);
                                  setSettingsMenuOpen(false);
                                  clearOrganizationContext();
                                  logout({ logoutParams: { returnTo: window.location.origin } });
                                }}
                                style={{
                                  background: '#ef4444',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '8px 16px',
                                  cursor: 'pointer',
                                  fontSize: 14,
                                  fontWeight: 600,
                                  width: '100%',
                                  textAlign: 'left'
                                }}
                              >
                                Logout
                              </button>
                            </div>
                          </>
                        )}

                        {!isAuthenticated && (
                          <div style={{ padding: '12px 24px' }}>
                            <button
                              onClick={() => {
                                setMenuOpen(false);
                                const returnTo = '/analytics?pre=1';
                                sessionStorage.setItem('postLoginReturnTo', returnTo);
                                loginWithRedirect({
                                  appState: {
                                    returnTo,
                                  },
                                });
                              }}
                              style={{
                                background: '#2563eb',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                padding: '8px 16px',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 600,
                                width: '100%',
                                textAlign: 'left'
                              }}
                            >
                              Login
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* Desktop Navigation */
                  <nav style={{ display: 'flex', alignItems: 'center' }}>
                    {isStudentOnlyAccount ? (
                      <>
                        <Link to="/" style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Home</Link>
                        <Link to={orgAwarePath('/analytics')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Dashboard</Link>
                        <Link to={orgAwarePath('/courses')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Courses</Link>
                        <Link to={orgAwarePath('/practice')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Questions</Link>
                        <Link to={orgAwarePath('/assignments')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Assignments</Link>
                        <Link to={orgAwarePath('/practice-tests')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Practice Tests</Link>
                        <Link to={orgAwarePath('/my-enrollments')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>My Enrollments</Link>
                        <Link to={orgAwarePath('/blogs')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Blogs</Link>
                        <Link to={orgAwarePath('/faq')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Faq</Link>
                        <Link to={orgAwarePath('/contact')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Contact Us</Link>
                      </>
                    ) : (
                      <>
                        <Link to="/" style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Home</Link>
                        <Link to={orgAwarePath('/courses')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Courses</Link>
                        <Link to={orgAwarePath('/practice')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Questions</Link>
                        <Link to={orgAwarePath('/assignments')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Assignments</Link>
                        <Link to={orgAwarePath('/practice-tests')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Practice Tests</Link>
                        <Link to={orgAwarePath('/blogs')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Blogs</Link>
                        <Link to={orgAwarePath('/faq')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Faq</Link>
                        <Link to={orgAwarePath('/contact')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Contact Us</Link>
                        <Link to={orgAwarePath('/analytics')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>Analytics</Link>
                      </>
                    )}

                    {/* Authenticated user links */}
                    {isAuthenticated && (
                      <div
                        style={{ position: 'relative', marginRight: 24 }}
                        onMouseEnter={() => setSettingsMenuOpen(true)}
                        onMouseLeave={() => setSettingsMenuOpen(false)}
                      >
                        <div style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }}>
                          Settings {settingsMenuOpen ? '▲' : '▼'}
                        </div>
                        {settingsMenuOpen && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            backgroundColor: '#333',
                            minWidth: '200px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            borderRadius: '4px',
                            marginTop: '0',
                            zIndex: 1000,
                            padding: '8px 0'
                          }}>
                            {visibleSettingsLinks.map((link) => (
                              <Link
                                key={link.to}
                                to={link.to}
                                style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 20px', fontSize: 14, whiteSpace: 'nowrap' }}
                              >
                                {link.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {isAuthenticated && (
                      <button
                        onClick={() => {
                          clearOrganizationContext();
                          logout({ logoutParams: { returnTo: window.location.origin } });
                        }}
                        style={{
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '6px 16px',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 600
                        }}
                      >
                        Logout
                      </button>
                    )}

                    {!isAuthenticated && (
                      <button
                        onClick={() => {
                          const returnTo = '/analytics?pre=1';
                          sessionStorage.setItem('postLoginReturnTo', returnTo);
                          loginWithRedirect({
                            appState: {
                              returnTo,
                            },
                          });
                        }}
                        style={{
                          background: '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '8px 20px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 14
                        }}
                      >
                        Login
                      </button>
                    )}
                  </nav>
                )}
              </div>
            </header>
          )}

          {shouldRenderMinimalTopBar && (
            <header style={{
              background: '#222',
              color: '#fff',
              padding: '0 0 0 0',
              marginBottom: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                maxWidth: 1200,
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                height: 64
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                  <img
                    src="/Icon-ExamMasters2.png"
                    alt="Exam Masters"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    fontWeight: 700,
                    fontSize: 22,
                    letterSpacing: 1,
                    whiteSpace: 'nowrap'
                  }}>Exam Masters</div>
                </div>

                <nav style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Link to="/analytics?pre=1" style={{ color: '#fff', textDecoration: 'none', fontSize: 16, fontWeight: 600 }}>Dashboard</Link>
                  {isAuthenticated ? (
                    <button
                      onClick={() => {
                        clearOrganizationContext();
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                      style={{
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '6px 16px',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600
                      }}
                    >
                      Logout
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const returnTo = '/analytics?pre=1';
                        sessionStorage.setItem('postLoginReturnTo', returnTo);
                        loginWithRedirect({
                          appState: {
                            returnTo,
                          },
                        });
                      }}
                      style={{
                        background: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '8px 20px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14
                      }}
                    >
                      Login
                    </button>
                  )}
                </nav>
              </div>
            </header>
          )}

          {/* Main content with routes */}
          <main style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={
                <Home
                  user={user}
                  loginWithRedirect={loginWithRedirect}
                  getAccessTokenSilently={getAccessTokenSilently}
                />
              } />
            
              <Route path="/courses" element={<Courses />} />
              <Route path="/:organizationSlug/courses" element={withStudentLayout(<Courses />)} />
              <Route path="/courses/:courseRef" element={<Courses />} />
              <Route path="/:organizationSlug/courses/:courseRef" element={withStudentLayout(<Courses />)} />
              <Route path="/course-details/:courseId" element={<CourseDetails />} />
              <Route path="/:organizationSlug/course-details/:courseId" element={withStudentLayout(<CourseDetails />)} />
              <Route path="/practice" element={<PracticeTasks />} />
              <Route path="/:organizationSlug/practice" element={withStudentLayout(<PracticeTasks />)} />
              <Route path="/assignments" element={<AssignmentsHub />} />
              <Route path="/:organizationSlug/assignments" element={withStudentLayout(<AssignmentsHub />)} />
              <Route path="/assignments/homework" element={<AssignmentHomework />} />
              <Route path="/:organizationSlug/assignments/homework" element={withStudentLayout(<AssignmentHomework />)} />
              <Route path="/assignments/:assignmentId/homework" element={<AssignmentHomework />} />
              <Route path="/:organizationSlug/assignments/:assignmentId/homework" element={withStudentLayout(<AssignmentHomework />)} />
              <Route path="/practice-tests" element={<PracticeTests />} />
              <Route path="/:organizationSlug/practice-tests" element={withStudentLayout(<PracticeTests />)} />
              <Route path="/practice-tests/:courseId" element={<PracticeTests />} />
              <Route path="/:organizationSlug/practice-tests/:courseId" element={withStudentLayout(<PracticeTests />)} />
              <Route path="/exam-center/:id" element={<ExamCenter />} />
              <Route path="/:organizationSlug/exam-center/:id" element={withStudentLayout(<ExamCenter />)} />
              <Route path="/exam/:id" element={<ExamQuiz />} />
              <Route path="/:organizationSlug/exam/:id" element={withStudentLayout(<ExamQuiz />)} />
              <Route path="/exam-attempt/:attemptId" element={<ExamAttemptReview />} />
              <Route path="/:organizationSlug/exam-attempt/:attemptId" element={withStudentLayout(<ExamAttemptReview />)} />
              <Route path="/practice-test/:id" element={<PracticeTestQuiz />} />
              <Route path="/:organizationSlug/practice-test/:id" element={withStudentLayout(<PracticeTestQuiz />)} />
              <Route path="/practice-result/:attemptId" element={<PracticeTestResults />} />
              <Route path="/:organizationSlug/practice-result/:attemptId" element={withStudentLayout(<PracticeTestResults />)} />
              <Route path="/analytics" element={<OrganizationPreDashboard />} />
              <Route path="/:organizationSlug/analytics" element={withStudentLayout(<Dashboard />)} />
              <Route path="/organization-setup" element={<OrganizationOnboarding />} />
              <Route path="/organization-invite" element={<OrganizationInviteAccept />} />
              <Route path="/my-sessions" element={withStudentLayout(<MySessions />)} />
              <Route path="/:organizationSlug/my-sessions" element={withStudentLayout(<MySessions />)} />
              <Route path="/my-session/:id" element={withStudentLayout(<MySessionPage />)} />
              <Route path="/:organizationSlug/my-session/:id" element={withStudentLayout(<MySessionPage />)} />
              <Route path="/my-enrollments" element={<MyEnrollments />} />
              <Route path="/:organizationSlug/my-enrollments" element={withStudentLayout(<MyEnrollments />)} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/:organizationSlug/faq" element={withStudentLayout(<FAQ />)} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/:organizationSlug/contact" element={withStudentLayout(<ContactUs />)} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/:organizationSlug/blogs" element={withStudentLayout(<Blogs />)} />
              <Route path="/blogs/:slug" element={<BlogArticle />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/profile" element={<MyProfile />} />
              <Route path="/:organizationSlug/profile" element={withStudentLayout(<MyProfile />)} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/:organizationSlug/settings" element={withStudentLayout(<Settings />)} />
              <Route path="/admin" element={withAdminLayout(<AdminDashboard />)} />
              <Route path="/super-admin" element={withAdminLayout(<SuperAdminDashboard />)} />
              <Route path="/teacher-dashboard" element={withAdminLayout(<TeacherDashboard />)} />
              <Route path="/manage-categories" element={withAdminLayout(<ManageCategories />)} />
              <Route path="/manage-categories-v2" element={withAdminLayout(<ManageCategoriesV2 />)} />
              <Route path="/manage-classrooms" element={withAdminLayout(<ManageClassrooms />)} />
              <Route path="/manage-assignments" element={withAdminLayout(<ManageAssignments />)} />
              <Route path="/manage-submissions" element={withAdminLayout(<ManageSubmissions />)} />
              <Route path="/manage-teacher-student-links" element={withAdminLayout(<ManageTeachingAssignments />)} />
              <Route path="/manage-teaching-assignments" element={withAdminLayout(<ManageTeachingAssignments />)} />
              <Route path="/manage-teacher-course-assignments" element={withAdminLayout(<ManageTeacherCourseAssignments />)} />
              <Route path="/manage-assignment-builder" element={withAdminLayout(<ManageAssignments />)} />
              <Route path="/manage-schedule" element={withAdminLayout(<ManageSchedule />)} />
              <Route path="/manage-schedule/all" element={withAdminLayout(<ManageSchedule />)} />
              <Route path="/manage-schedule/mysessions" element={withAdminLayout(<ManageSchedule />)} />
              <Route path="/manage-schedule/new" element={withAdminLayout(<ManageScheduleDetail />)} />
              <Route path="/manage-schedule/:id" element={withAdminLayout(<ManageScheduleDetail />)} />
              <Route path="/manage-sessions/all" element={withAdminLayout(<ManageSessions />)} />
              <Route path="/manage-sessions/mysessions" element={withAdminLayout(<ManageSessions />)} />
              <Route path="/manage-sessions/:id" element={withAdminLayout(<ManageSessionDetail />)} />
              <Route path="/admin/submissions/:attemptId" element={withAdminLayout(<SubmissionReview />)} />
              <Route path="/manage-courses" element={withAdminLayout(<ManageCourses />)} />
              <Route path="/manage-course-pricing" element={withAdminLayout(<ManageCoursePricing />)} />
              <Route path="/manage-exams" element={withAdminLayout(<ManageExams />)} />
              <Route path="/manage-exams/:examId/questions" element={withAdminLayout(<ManageExams />)} />
              <Route path="/manage-questions" element={withAdminLayout(<ManageQuestions />)} />
              <Route path="/manage-diagrams" element={withAdminLayout(<ManageDiagrams />)} />
              <Route path="/manage-category-diagrams" element={withAdminLayout(<ManageCategoryDiagrams />)} />
              <Route path="/manage-dynamic-components" element={withAdminLayout(<ManageDynamicComponents />)} />
              <Route path="/manage-site-content" element={withAdminLayout(<ManageSiteContent />)} />
              <Route path="/manage-free-resources" element={withAdminLayout(<ManageFreeResources />)} />
              <Route path="/manage-documents" element={withAdminLayout(<ManageDocuments />)} />
              <Route path="/manage-email-templates" element={withAdminLayout(<ManageEmailTemplates />)} />
              <Route path="/reports/teacher-hours" element={withAdminLayout(<ReportTeacherHours />)} />
              <Route path="/reports/student-sessions" element={withAdminLayout(<ReportStudentSessions />)} />
              <Route path="/manage-organizations" element={withAdminLayout(<ManageOrganizations />)} />
              <Route path="/manage-organization-memberships" element={withAdminLayout(<ManageOrganizationMemberships />)} />
              <Route path="/manage-organization-membership-roles" element={withAdminLayout(<ManageOrganizationMembershipRoles />)} />
              <Route path="/manage-organization-members-and-roles" element={withAdminLayout(<ManageOrganizationMembersAndRoles />)} />
              <Route path="/manage-staff" element={withAdminLayout(<ManageOrganizationMembersAndRoles />)} />
              <Route path="/manage-teachers" element={withAdminLayout(<ManageOrganizationMembersAndRoles />)} />
              <Route path="/manage-students" element={withAdminLayout(<ManageOrganizationMembersAndRoles />)} />
              <Route path="/manage-organization-requests" element={withAdminLayout(<ManageOrganizationRequests />)} />
              <Route path="/manage-platform-user-roles" element={withAdminLayout(<ManagePlatformUserRoles />)} />
              <Route path="/super-admin/platform-users" element={withAdminLayout(<SuperAdminPlatformUsers />)} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/:organizationSlug/billing" element={withStudentLayout(<Billing />)} />
              <Route path="/books" element={withAdminLayout(<BookManagement />)} />
              <Route path="/subscription-success" element={<SubscriptionSuccess />} />
              
              {/* Catch-all route - redirect any unmatched routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer style={{
            background: '#222',
            color: '#fff',
            textAlign: 'center',
            padding: '18px 0',
            marginTop: 'auto'
          }}>
            <div style={{ marginBottom: 8 }}>
              <Link to="/privacy" style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Privacy Policy</Link>
              <Link to="/terms" style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Terms of Service</Link>
              <Link to="/data-deletion" style={{ color: '#a5b4fc', fontSize: 13, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Data Deletion</Link>
            </div>
            &copy; {new Date().getFullYear()} ExamMasters &mdash; All rights reserved.
          </footer>
          
        </div>
      </Router>
    </HomeProvider>
  );
}

export default App;